import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Button } from '@arco-design/web-react';
import { IconRefresh, IconHome } from '@arco-design/web-react/icon';
import styles from './index.module.less';

import logger from '@/utils/logger';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
}

/**
 * React ErrorBoundary 组件
 * 捕获 React 组件渲染过程中的异常，防止页面白屏
 */
class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // 更新 state，下次渲染时显示错误 UI
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // 记录错误信息
    logger.error('[ErrorBoundary] React 组件渲染异常:', error);
    logger.error('[ErrorBoundary] 错误堆栈:', errorInfo.componentStack);

    // 上报错误信息（可选）
    this.reportError(error, errorInfo);
  }

  private reportError = (error: Error, errorInfo: ErrorInfo) => {
    try {
      // 这里可以集成错误上报服务，如 Sentry
      const errorData = {
        message: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack,
        timestamp: new Date().toISOString(),
        url: window.location.href,
        userAgent: navigator.userAgent,
      };

      logger.warn('[ErrorBoundary] 错误上报数据:', errorData);

      // 可以发送到后端或第三方服务
      // fetch('/api/error-report', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify(errorData),
      // }).catch(console.warn);
    } catch (reportError) {
      logger.warn('[ErrorBoundary] 错误上报失败:', reportError);
    }
  };

  private handleRefresh = () => {
    // 刷新页面
    window.location.reload();
  };

  private handleGoHome = () => {
    // 返回首页（保留 layout2 / layout3 / layout4 主题参数）
    const currentParams = new URLSearchParams(window.location.search);
    const layout2 = currentParams.get('layout2') === 'true';
    const layout3 = currentParams.get('layout3') === 'true';
    const layout4 = currentParams.get('layout4') === 'true';

    const params = new URLSearchParams();
    if (layout2) params.set('layout2', 'true');
    if (layout3) params.set('layout3', 'true');
    if (layout4) params.set('layout4', 'true');

    const origin = window.location.origin;
    const url = params.toString() ? `${origin}/?${params.toString()}` : `${origin}/`;
    window.location.href = url;
  };

  private handleRetry = () => {
    // 重置错误状态，尝试重新渲染
    this.setState({ hasError: false });
  };

  render() {
    if (this.state.hasError) {
      // 如果有自定义 fallback，使用自定义的
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // 默认错误 UI
      return (
        <div className={styles.errorBoundaryContainer}>
          <div className={styles.errorContent}>
            <div className={styles.errorTitle}>页面出现异常</div>
            <div className={styles.errorSubtitle}>
              抱歉，页面遇到了一个意外错误。请尝试刷新页面或返回首页。
            </div>
            <div className={styles.errorActions}>
              <Button
                type="primary"
                icon={<IconRefresh />}
                onClick={this.handleRetry}
                className={styles.errorActionButton}
              >
                重试
              </Button>
              <Button
                icon={<IconRefresh />}
                onClick={this.handleRefresh}
                className={styles.errorActionButton}
              >
                刷新页面
              </Button>
              <Button
                icon={<IconHome />}
                onClick={this.handleGoHome}
                className={styles.errorActionButton}
              >
                返回首页
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
